import { notification } from 'antd'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import { AreaContext } from 'pages/ProjectsPage/WorkspacePage/components/Common/AreaContext'
import { useCallback, useContext } from 'react'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { useTrackChangesQuery } from '../../../components/TrackChangesPanel/api/useTrackChangesQuery'
import { useHoveredActionMutationService } from '../../../services'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import { usePTActivationDialogActions, usePTActivationStore } from '../store'

/**
 * Internal hook: Handles bulk content activation with optimistic updates and rollback
 */
export const usePTContentActivation = () => {
    // Use Workspace2Store for projectId (performance optimized), AreaContext for nugget
    const projectId = useWorkspace2Store((state) => state.projectId)
    const { nugget } = useContext(AreaContext)
    const {
        mutateDeactivationBulkWithApolloClient,
    } = useHoveredActionMutationService()
    const { fetchTrackChangesData } = useTrackChangesQuery()
    // The hook useProductFetcherUtils requires an options object, even if empty, to initialize correctly.
    const { appendBodContentIfNoBodPill } = useProductFetcherUtils({})

    const {
        closeDialog: closePTActivationDialog,
    } = usePTActivationDialogActions()
    const closeProgressDialog = usePTActivationStore(
        (state) => state.closeProgressDialog
    )

    const bulkActivateItems = useCallback(
        async (
            itemsToActivate: Partial<ContentItemViewNodesType>[],
            selectedProjectElementNodeId?: string
        ) => {
            const inputs = itemsToActivate.map((item) => ({
                documentTemplateViewId: item.documentTemplateViewId,
                isHidden: false,
                contentItemOwnerId: item.dtvItemId,
                projectId,
                nodeId: selectedProjectElementNodeId,
                contentItemId: item.id,
                projectNuggetId: nugget?.id,
                isTrackChangeToggleOn: true,
                ...(item.isDeactivated && {
                    isContentDeactivatedInOM: false,
                }),
            }))

            try {
                await mutateDeactivationBulkWithApolloClient(inputs)
            } catch (error) {
                throw error
            }
        },
        [projectId, nugget?.id, mutateDeactivationBulkWithApolloClient]
    )

    /**
     * Activates content items with optimistic update and rollback on error
     */
    const activateContentWithRollback = useCallback(
        async (
            itemsToActivate: Partial<ContentItemViewNodesType>[],
            selectedProjectElementNodeId?: string,
            idsToRemove?: Set<string>
        ) => {
            const storeState = useWorkspace2Store.getState()
            const latestNodes = storeState.contentItemViewNodes
            const setContentItemViewNodes =
                storeState.actions.setContentItemViewNodes

            const idsToActivate = new Set(
                itemsToActivate
                    .map((item) => item.id)
                    .filter((id): id is string => !!id)
            )

            // Single optimistic update for all items.
            // Filter out old frontend children being replaced by backend nodes (idsToRemove).
            const updatedNodes = latestNodes
                .filter((node) => !node.id || !idsToRemove?.has(node.id))
                .map((node) => {
                    if (node.id && idsToActivate.has(node.id)) {
                        // For other content (PT content, paragraphs, etc): apply activation updates
                        const updates: Partial<ContentItemViewNodesType> = {
                            isHidden: false,
                        }
                        // Clear isDeactivated flag if item was deactivated
                        if (node.isDeactivated) {
                            updates.isDeactivated = false
                        }
                        // Set isAddedToProject to true for PT content only (matches backend)
                        // Only for nodes that are actually PT-related, not all ancestors
                        if (node.isProductType || node.isForProductType) {
                            updates.isAddedToProject = true
                        }
                        return { ...node, ...updates }
                    }
                    return node
                })

            // IDs that will remain in the store after idsToRemove filter.
            // Used to detect truly new backend nodes at any nesting depth.
            const remainingNodeIds = new Set(
                updatedNodes.map((n) => n.id).filter(Boolean)
            )

            // Append nodes that aren't already in the remaining store.
            // Only take isHidden=false items — guarantees we only add backend-provided
            // (already-active) nodes, never old hidden frontend versions being replaced
            // by new compound-ID backend nodes at any nesting level.
            const newNodes = itemsToActivate.filter(
                (item) =>
                    item.id &&
                    !remainingNodeIds.has(item.id) &&
                    item.isHidden === false
            )

            const mergedNodes = appendBodContentIfNoBodPill(
                Array.from(
                    new Map(
                        [...updatedNodes, ...newNodes].map((n) => [n.id, n])
                    ).values()
                )
            )

            setContentItemViewNodes(mergedNodes)
            closeProgressDialog() // Close progress dialog after optimistic update
            closePTActivationDialog()

            // Single bulk mutation for all items with rollback on error
            try {
                await bulkActivateItems(
                    itemsToActivate,
                    selectedProjectElementNodeId
                )
                // Refresh track changes after successful activation
                await fetchTrackChangesData()
            } catch (bulkError) {
                setContentItemViewNodes(latestNodes)
                notification.error({
                    message: 'Failed to Activate PT-Keyed Content',
                    description:
                        'Could not activate content items linked to the Product Types. Please try again.',
                    duration: 5,
                    placement: 'topRight',
                })
                throw bulkError
            }
        },
        [
            bulkActivateItems,
            fetchTrackChangesData,
            closePTActivationDialog,
            closeProgressDialog,
            appendBodContentIfNoBodPill,
        ]
    )

    return {
        activateContentWithRollback,
        bulkActivateItems,
    }
}
